import { initContract } from "@ts-rest/core";
import { z } from "zod";
import { OrganizationUserRole } from "@common/userOrganizationRoles";
import { ErrorMessageSchema } from "@common/schemas/error.schema";
import {
    organizationClientUserSchema,
    organizationClientUsersQuerySchema,
} from "@common/schemas/domains/organizationMember/organization-client-user.schema";
import { OrganizationMemberDto } from "../dto/organization/organization-member.dto";
import { OrganizationInviteDto } from "../dto/organization/organization-invite.dto";

const c = initContract();

export const organizationMemberContract = c.router(
    {
        getMembers: {
            method: "GET",
            path: "/",
            responses: {
                200: c.type<OrganizationMemberDto[]>(),
            },
        },
        removeMember: {
            method: "DELETE",
            path: "/:memberId",
            pathParams: z.object({
                memberId: z.coerce.number(),
            }),
            responses: {
                200: c.noBody(),
            },
        },
        getClientUsers: {
            method: "GET",
            path: "/client-users",
            pathParams: z.object({
                organizationId: z.string().uuid(),
            }),
            query: organizationClientUsersQuerySchema,
            responses: {
                200: z.array(organizationClientUserSchema),
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        getInvites: {
            method: "GET",
            path: "/invites",
            responses: {
                200: c.type<OrganizationInviteDto[]>(),
            },
        },
        inviteMember: {
            method: "POST",
            path: "/invite",
            body: z.object({
                emails: z.array(z.string().email()),
                role: z.nativeEnum(OrganizationUserRole),
                workspaceAssignments: z.array(z.string().uuid()),
                assignToNewClients: z.boolean(),
            }),
            responses: {
                201: c.noBody(),
            },
        },
        resendInvite: {
            method: "POST",
            path: "/invite/:inviteId/resend",
            pathParams: z.object({
                inviteId: z.coerce.number(),
            }),
            body: c.noBody(),
            responses: {
                200: c.noBody(),
            },
        },
        cancelInvite: {
            method: "DELETE",
            path: "/invite/:inviteId",
            pathParams: z.object({
                inviteId: z.coerce.number(),
            }),
            responses: {
                200: c.noBody(),
            },
        },
        updateMemberPermissions: {
            method: "PUT",
            path: "/:memberId",
            pathParams: z.object({
                memberId: z.coerce.number(),
            }),
            body: z.object({
                role: z.nativeEnum(OrganizationUserRole),
                workspaceAssignments: z.array(z.string().uuid()),
                assignToNewClients: z.boolean(),
            }),
            responses: {
                200: c.noBody(),
            },
        },
        transferOwner: {
            method: "POST",
            path: "/:memberId/transfer-owner",
            pathParams: z.object({
                memberId: z.coerce.number(),
            }),
            body: c.noBody(),
            responses: {
                200: c.noBody(),
                400: ErrorMessageSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
    },
    {
        pathPrefix: "/organization-member/:organizationId",
    },
);
